import { FilterTableButton } from '@shared/components/FilterTableButton';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import DateRangeFilters from '@shared/components/DateRangeFilters';
import FilterButtons from '@shared/components/FilterButtons';
import { DayJsRange, getButtonStyle, typedEntries, VisitListApplyFilter } from '@shared/entity';
import { FULL_PAGE_TABLE_CONFIG, VISIT_FILTER_BUTTONS_CONFIG } from '../../entity';
import { createVisitsColumns } from './columns';
import VisitsListTable from './visits-list-table';
import VisitDailyOverviewDiagramDialog from '../visit-daily-overview-diagram/visit-daily-overview-diagram-dialog';
import { useVisitRows, useQuickDateFilter, QUICK_DATE_FILTERS } from './visits-list.hooks';

type VisitsListProps = {
  showFiltersFor: VisitListApplyFilter;
  type: VisitListApplyFilter;
};

const VisitsList = (props: VisitsListProps) => {
  const [selectedTimelineDate, setSelectedTimelineDate] = useState<Date | null>(null);

  const handleDateHeaderClick = (dateLabel: string) => {
    const parsed = dayjs(dateLabel, ['DD.MM.YYYY', 'D.M.YYYY', 'DD/MM/YYYY']);
    if (parsed.isValid()) {
      setSelectedTimelineDate(parsed.toDate());
    }
  };

  const columns = createVisitsColumns(handleDateHeaderClick);

  const { rows, visitListFilters, updateVisitFilters } = useVisitRows(props.type);

  const { control, setValue } = useForm<Partial<DayJsRange>>({
    defaultValues: { from: visitListFilters.dates.from, to: visitListFilters.dates.to },
  });

  const { activeFilter, apply: applyQuickFilter } = useQuickDateFilter(
    updateVisitFilters,
    setValue,
  );

  return (
    <Stack spacing={4}>
      {props.showFiltersFor === 'dashBoardVisitOverView' && (
        <Stack direction="row" spacing={2} mb={2}>
          {typedEntries(QUICK_DATE_FILTERS).map(([key]) => (
            <FilterTableButton
              key={key}
              text={QUICK_DATE_FILTERS[key].label}
              variant={getButtonStyle(activeFilter, key)}
              setTableView={() => applyQuickFilter(key)}
            />
          ))}
        </Stack>
      )}
      {props.showFiltersFor === 'allVisitsPage' && (
        <FilterButtons
          filterButtonsConfig={VISIT_FILTER_BUTTONS_CONFIG}
          activeFilter={visitListFilters.view}
          onChange={key => updateVisitFilters?.(draft => (draft.view = key))}
        />
      )}
      <Stack spacing={2} height="100%">
        <DateRangeFilters
          control={control}
          onChangeFrom={date => {
            updateVisitFilters?.(draft => {
              draft.dates.from = date?.toISOString();
            });
          }}
          onChangeTo={date => {
            updateVisitFilters?.(draft => {
              draft.dates.to = date?.toISOString();
            });
          }}
        />
        <VisitsListTable
          rows={rows}
          columns={columns}
          {...FULL_PAGE_TABLE_CONFIG}
          visitListApplyFilter={props.type}
          onHeaderRowClick={handleDateHeaderClick}
        />
      </Stack>
      <VisitDailyOverviewDiagramDialog
        onClose={() => setSelectedTimelineDate(null)}
        selectedTimelineDate={selectedTimelineDate}
      />
    </Stack>
  );
};

export default VisitsList;
